import auth from './auth'
import {
    ActionRecord,
    DataResponse,
    EntityData,
    EntityType,
    MetaEntry,
    MultiTarget,
    ReviewResponse,
    Workflow,
    User,
    EntryReport,
    ActionReport,
    GetCycleResponse,
    DuplicateRow,
    SelectEnteries,
} from './types'
import getId from './get-id'

let protocol = String(process.env.REACT_APP_API_BASE).startsWith('localhost')
    ? 'http://'
    : 'https://'
const apiBase = protocol + process.env.REACT_APP_API_BASE

export function authHeader(): HeadersInit {
    if (auth.token) return { Authorization: 'Bearer ' + auth.token }
    return {}
}

class CustomError extends Error {
    status: number
    data?: any

    constructor(message: string, status: number, data?: any) {
        super(message)
        this.name = 'CustomError'
        this.status = status
        this.data = data
    }
}

const jsonHeader = { 'Content-Type': 'application/json; charset=UTF-8' }

async function makeRequest(method: string, path: string, signal?: AbortSignal, payload?: object) {
    let response = await fetch(apiBase + path, {
        method,
        body: payload && JSON.stringify(payload),
        headers: { ...authHeader(), ...jsonHeader },
        signal,
    })
    if (response.status === 401) {
        const { errorMessage = 'Invalid auth' } = await response.json()
        throw new Error(errorMessage)
    }
    if (response.headers.get('content-type')?.includes('application/json')) {
        let json = await response.json()
        if (!response.ok) {
            if (json.data) {
                throw new CustomError(json.errorMessage, response.status, json.data)
            }
            throw new Error(json.errorMessage)
        }
        return json
    } else {
        let text = await response.text()
        if (!response.ok) throw new Error(text)
        return text
    }
}

async function post<T>(path: string, payload?: object, signal?: AbortSignal) {
    return (await makeRequest('POST', path, signal, payload)) as T
}

async function put<T>(path: string, payload: object, signal?: AbortSignal) {
    return (await makeRequest('PUT', path, signal, payload)) as T
}

// async function patch(path: string, payload: object) {
//     return makeRequest('PATCH', path, undefined, payload);
// }
//
// async function del(path: string) {
//     return makeRequest('DELETE', path, undefined);
// }

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return (await makeRequest('GET', path, signal)) as T
}

const user = {
    login: async (email: string, password: string) =>
        post<string>('/api/user/login', { email, password }),
    list: async (search = '', status = '') =>
        get<User[]>(`/api/users?search=${search}&status=${status}`),
    create: async (user: User & { password: string }) => post<User>('/api/users', { user }),
    update: async (
        id: number,
        changes: {
            name?: string
            email?: string
            password?: string
            role?: string
            status?: number
        },
    ) =>
        put<{ name?: string; email?: string; password?: string; role?: string; status?: number }>(
            `/api/users/${id}`,
            changes,
        ),
}

const workflows = {
    list: async (status: any, search: string) =>
        get<Array<Workflow>>('/api/workflows?status=' + status + '&search=' + search),
    get: async (id: number) => get<Workflow>('/api/workflows/' + id),
    validate: async (config: string) =>
        post<{ valid: boolean }>('/api/workflows/validate', { config }),
    save: async (
        id: number,
        config: string,
        status: any,
        contributors: SelectEnteries[],
        reviewers: SelectEnteries[],
    ) => put('/api/workflows/' + id, { config, status, contributors, reviewers }),
    create: async (config: string, contributors: SelectEnteries[], reviewers: SelectEnteries[], status: any) =>
        post<{ id: number }>('/api/workflows', { config, contributors, reviewers, status }),
}

const tasks = {
    list: async (id: number, idx: number, page: number, viewType: string, search: string) =>
        get<DataResponse | ReviewResponse>(
            `/api/workflows/${id}/steps/${idx}/tasks?page=${page}&viewType=${viewType}&search=${search}`,
        ),
    approve: async (id: number, idx: number, user: string, objId: number, srcId?: number) =>
        post(`/api/workflows/${id}/steps/${idx}/tasks/approve`, { user, objId, srcId }),
    flagError: async (id: number, idx: number, user: string, objId: number, note?: string) =>
        post(`/api/workflows/${id}/steps/${idx}/tasks/flagError`, {
            user,
            objId,
            note,
        }),
    markAsDuplicate: async (id: number, idx: number, user: string, objId: number, note?: string) =>
        post(`/api/workflows/${id}/steps/${idx}/tasks/markDuplicate`, {
            user,
            objId,
            note,
        }),
    finish: async (id: number, idx: number) =>
        post(`/api/workflows/${id}/steps/${idx}/tasks/finish`, undefined),
    getNext: async (id: number) =>
        get<MetaEntry>(`/api/workflows/${id}/tasks/next-correction-task`),
    cancelTask: async (id: number, objId: number) =>
        post(`/api/workflows/${id}/tasks/cancel-correction-task/${objId}`),
}

const data = {
    page: async (id: number, idx: number, page: number, viewType: string, search?: string) =>
        get<DataResponse>(
            `/api/workflows/${id}/steps/${idx}/data?page=${page}&viewType=${viewType}&search=${search}`,
        ),
    get: async (id: number, idx: number, rowId: number, srcId: number) =>
        get<{ row: any; meta: MetaEntry; correction?: any }>(`/api/workflows/${id}/steps/${idx}/data/${rowId}/${srcId}`),
    getUser: async (id: number, idx: number, user: string, rowId: number, srcId: number) =>
        get<{ row: any; meta: MetaEntry; review?: ActionRecord; prevRow?: any; similarRow?: any; similarRowMeta?: MetaEntry, similarRowReview?: ActionRecord, duplicate?: DuplicateRow }>(
            `/api/workflows/${id}/steps/${idx}/data/${user}/${rowId}/${srcId}`,
        ),
    getReport: async (id: number, start: string, end: string) =>
        get<{ entries: EntryReport[]; actionReport: ActionReport[]; percentComplete?: number }>(
            `/api/workflows/${id}/data/reporting?start=${start}&end=${end}`,
        ),
    getDuplicates: async (id: number) =>
        get<{ duplicates: DuplicateRow[]; rows: any[]; meta: MetaEntry[] }>(
            `/api/workflows/${id}/data/duplicates`,
        ),
    deleteDuplicates: async (id: number, duplicates: DuplicateRow[]) =>
        post(`/api/workflows/${id}/data/delete-duplicates`, { duplicates }),
    save: async (id: number, idx: number, srcId: number, data: any, note?: string, sources?: string) =>
        put(`/api/workflows/${id}/steps/${idx}/data/${getId(data)}/${srcId}`, { data, note, sources }),
    insert: async (
        id: number,
        idx: number,
        _srcId: number,
        data: any,
        note?: string,
        sources?: string,
        overrideDuplicate?: boolean,
    ) => post(`/api/workflows/${id}/steps/${idx}/data`, { data, note, sources, overrideDuplicate }),
    getHistory: async (workflowId: number, rowId: number, cycleId?: number) =>
        get<any>(`/api/workflows/${workflowId}/history/${rowId}`),
}

const cycles = {
    getByWorkflow: async (id: number) => get<GetCycleResponse>(`/api/cycle/workflow/${id}`),
    create: async (id: number) => post(`/api/cycle`, { workflowId: id }),
    end: async (id: number) => put(`/api/cycle/workflow/${id}`, {}),
}

const entities = {
    search: (entityType: EntityType, query: string, signal?: AbortSignal) =>
        post<Array<EntityData>>('/api/entities', { ...entityType, query }, signal),
    get: (entityType: EntityType, id: number, signal?: AbortSignal) =>
        post<EntityData>('/api/entities/' + id, entityType, signal),
    listMulti: (id: number, target: MultiTarget, entity: EntityType, signal?: AbortSignal) =>
        post<Array<EntityData>>('/api/entities/multi/' + id, { target, entity }, signal),
    listMultiMP: (id: number, target: MultiTarget, entity: EntityType, signal?: AbortSignal) =>
        post<Array<EntityData>>('/api/entities/multi-mp/' + id, { target, entity }, signal),
    updateMulti: (
        id: number,
        target: MultiTarget,
        entityIds: Array<number>,
        signal?: AbortSignal,
    ) => put('/api/entities/multi/' + id, { target, entityIds }, signal),
    updateMultiMP: (
        id: number,
        target: MultiTarget,
        entities: Array<{ id: number; dataset: string }>,
        signal?: AbortSignal,
    ) => put('/api/entities/multi-mp/' + id, { target, entities }, signal),
}

export { user, workflows, tasks, data, entities, cycles }
